/**
 * ArrowAnnotation — renders an arrow from a start point to an end point.
 *
 * Supports configurable lineWidth, color, and arrowhead size.
 * Interactive: hit test on shaft, head, and individual handles (start/end).
 */

import { distSq, pointToSegmentDist } from '../geometry';
import type {
    AnnotationInteractionEvent,
    AnnotationStyle,
    ArrowAnnotationData,
    IAnnotationObject,
} from '../types';
import { AnnotationType, DEFAULT_ANNOTATION_STYLE } from '../types';

const HANDLE_RADIUS = 8;
const HEAD_LENGTH = 16;
const HEAD_ANGLE = Math.PI / 6;
const SHAFT_HIT_TOLERANCE = 6;

interface ArrowDragState {
    handle: string | null;
    startStart: { x: number; y: number };
    startEnd: { x: number; y: number };
}

export class ArrowAnnotation implements IAnnotationObject {
    readonly id: string;
    readonly interactive = true;
    visible = true;
    selected = false;
    readonly zIndex: number;

    position: { x: number; y: number };
    start: { x: number; y: number };
    end: { x: number; y: number };
    style: AnnotationStyle;

    hoveredHandle: 'start' | 'end' | 'body' | null = null;

    private dragState: ArrowDragState | null = null;

    constructor(options: {
        id: string;
        start: { x: number; y: number };
        end: { x: number; y: number };
        style?: Partial<AnnotationStyle>;
        zIndex?: number;
    }) {
        this.id = options.id;
        this.start = { ...options.start };
        this.end = { ...options.end };
        this.position = { ...options.start };
        this.style = { ...DEFAULT_ANNOTATION_STYLE, ...options.style };
        this.zIndex = options.zIndex ?? 100;
    }

    // ── Hit testing ──

    hitTest(x: number, y: number): boolean {
        return this.hitTestHandle(x, y) !== null || this.hitTestShaft(x, y);
    }

    getHandleAtPoint(x: number, y: number): 'start' | 'end' | 'body' | null {
        const handle = this.hitTestHandle(x, y);
        if (handle) return handle;
        if (this.hitTestShaft(x, y)) return 'body';
        return null;
    }

    hitTestHandle(x: number, y: number): 'start' | 'end' | null {
        if (distSq(x, y, this.start.x, this.start.y) <= HANDLE_RADIUS * HANDLE_RADIUS) {
            return 'start';
        }
        if (distSq(x, y, this.end.x, this.end.y) <= HANDLE_RADIUS * HANDLE_RADIUS) {
            return 'end';
        }
        return null;
    }

    private hitTestShaft(x: number, y: number): boolean {
        return (
            pointToSegmentDist(x, y, this.start.x, this.start.y, this.end.x, this.end.y) <=
            SHAFT_HIT_TOLERANCE + this.style.lineWidth / 2
        );
    }

    // ── Interaction ──

    clearHoverState(): void {
        this.hoveredHandle = null;
    }

    handleEvent(event: AnnotationInteractionEvent): boolean {
        switch (event.type) {
            case 'hover': {
                const handle = this.getHandleAtPoint(event.currentPoint.x, event.currentPoint.y);
                if (handle) {
                    this.hoveredHandle = handle;
                    return true;
                }
                return false;
            }
            case 'pointer_down': {
                const handle = this.getHandleAtPoint(event.currentPoint.x, event.currentPoint.y);
                if (!handle) return false;
                this.dragState = {
                    handle,
                    startStart: { ...this.start },
                    startEnd: { ...this.end },
                };
                return true;
            }
            case 'pointer_move': {
                if (!this.dragState) return false;
                const dx = event.currentPoint.x - event.startPoint.x;
                const dy = event.currentPoint.y - event.startPoint.y;
                const { handle, startStart, startEnd } = this.dragState;

                if (handle === 'start') {
                    this.start = { x: startStart.x + dx, y: startStart.y + dy };
                    this.position = { ...this.start };
                } else if (handle === 'end') {
                    this.end = { x: startEnd.x + dx, y: startEnd.y + dy };
                } else {
                    // body — move whole shape
                    this.start = { x: startStart.x + dx, y: startStart.y + dy };
                    this.end = { x: startEnd.x + dx, y: startEnd.y + dy };
                    this.position = { ...this.start };
                }
                return true;
            }
            case 'pointer_up': {
                this.dragState = null;
                return true;
            }
        }
    }

    // ── Placement ──

    updatePlacement(_startPoint: { x: number; y: number }, currentPoint: { x: number; y: number }): void {
        this.end = { ...currentPoint };
    }

    isValidPlacement(): boolean {
        const dx = this.end.x - this.start.x;
        const dy = this.end.y - this.start.y;
        return Math.sqrt(dx * dx + dy * dy) >= 3;
    }

    updateStyle(patch: Partial<AnnotationStyle>): void {
        Object.assign(this.style, patch);
    }

    // ── Serialisation ──

    serialize(): ArrowAnnotationData {
        return {
            type: AnnotationType.ARROW,
            id: this.id,
            start: { ...this.start },
            end: { ...this.end },
            style: { ...this.style },
        };
    }

    static hydrate(data: ArrowAnnotationData): ArrowAnnotation {
        return new ArrowAnnotation({
            id: data.id,
            start: data.start,
            end: data.end,
            style: data.style,
        });
    }

    // ── Rendering ──

    render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible) return;

        const { start, end, style } = this;
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len < 1) return;

        const angle = Math.atan2(dy, dx);

        ctx.save();
        ctx.strokeStyle = style.color;
        ctx.fillStyle = style.color;
        ctx.lineWidth = style.lineWidth;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';

        // Shaft — stop at the base of the arrowhead so the head sits on top of the line
        const headLen = Math.min(HEAD_LENGTH, len * 0.4);
        const headBackDist = headLen * Math.cos(HEAD_ANGLE);
        const shaftEndX = end.x - headBackDist * Math.cos(angle);
        const shaftEndY = end.y - headBackDist * Math.sin(angle);

        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(shaftEndX, shaftEndY);
        ctx.stroke();

        // Arrowhead
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(
            end.x - headLen * Math.cos(angle - HEAD_ANGLE),
            end.y - headLen * Math.sin(angle - HEAD_ANGLE)
        );
        ctx.lineTo(
            end.x - headLen * Math.cos(angle + HEAD_ANGLE),
            end.y - headLen * Math.sin(angle + HEAD_ANGLE)
        );
        ctx.closePath();
        ctx.fill();

        // Selection takes precedence: always show both handles + bounding box.
        if (this.selected) {
            this.renderBoundingBox(ctx);
            this.renderHandle(ctx, start.x, start.y);
            this.renderHandle(ctx, end.x, end.y);
        } else if (this.hoveredHandle === 'start' || this.hoveredHandle === 'end') {
            this.renderHandle(ctx, start.x, start.y);
            this.renderHandle(ctx, end.x, end.y);
        } else if (this.hoveredHandle === 'body') {
            this.renderBoundingBox(ctx);
        }

        ctx.restore();
    }

    private renderHandle(ctx: CanvasRenderingContext2D, x: number, y: number): void {
        ctx.beginPath();
        ctx.arc(x, y, HANDLE_RADIUS, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
    }

    private renderBoundingBox(ctx: CanvasRenderingContext2D): void {
        const pad = HANDLE_RADIUS;
        const minX = Math.min(this.start.x, this.end.x) - pad;
        const minY = Math.min(this.start.y, this.end.y) - pad;
        const maxX = Math.max(this.start.x, this.end.x) + pad;
        const maxY = Math.max(this.start.y, this.end.y) + pad;

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
    }
}
